import { CUSTOM_CASE_CHECKOUT_URL, CUSTOM_CASE_PRODUCT_PATH } from "./customCase";
import type { CustomLandingContext } from "./customLanding";

declare global {
  interface Window {
    fastspring?: {
      builder?: {
        reset?: () => void;
        add?: (productId: string) => void;
        checkout?: () => void;
        recognize?: (data: { email: string }) => void;
        tag?: (data: Record<string, string>) => void;
      };
    };
  }
}

export type FastSpringPopupClosePayload = {
  id?: string;
  reference?: string;
} | null;

export const isFastSpringPopupPurchaseComplete = (payload: FastSpringPopupClosePayload) =>
  Boolean(payload && (String(payload.id || "").trim() || String(payload.reference || "").trim()));

const appendCheckoutQuery = ({
  checkoutUrl,
  customOrderId,
  accessToken,
  landingContext,
}: {
  checkoutUrl: string;
  customOrderId?: string;
  accessToken?: string;
  landingContext?: Partial<CustomLandingContext>;
}) => {
  const normalizedUrl = String(checkoutUrl || "").trim();
  if (!normalizedUrl) return "";

  try {
    const url = new URL(normalizedUrl);
    if (customOrderId) url.searchParams.set("customOrderId", customOrderId);
    if (accessToken) url.searchParams.set("customOrderAccessToken", accessToken);
    if (landingContext?.lang) url.searchParams.set("lang", landingContext.lang);
    if (landingContext?.topic) url.searchParams.set("topic", landingContext.topic);
    if (landingContext?.source) url.searchParams.set("source", landingContext.source);
    if (landingContext?.utmSource) url.searchParams.set("utm_source", landingContext.utmSource);
    if (landingContext?.utmMedium) url.searchParams.set("utm_medium", landingContext.utmMedium);
    if (landingContext?.utmCampaign) url.searchParams.set("utm_campaign", landingContext.utmCampaign);
    return url.toString();
  } catch {
    return normalizedUrl;
  }
};

export const launchCustomCheckout = ({
  email,
  customOrderId,
  accessToken,
  landingContext,
  productPath = CUSTOM_CASE_PRODUCT_PATH,
  checkoutUrl = CUSTOM_CASE_CHECKOUT_URL,
}: {
  email?: string;
  customOrderId?: string;
  accessToken?: string;
  landingContext?: Partial<CustomLandingContext>;
  productPath?: string;
  checkoutUrl?: string;
}) => {
  const fastspringBuilder = window.fastspring?.builder;
  if (fastspringBuilder?.add && fastspringBuilder?.checkout) {
    if (email && fastspringBuilder.recognize) {
      fastspringBuilder.recognize({ email });
    }
    fastspringBuilder.reset?.();
    fastspringBuilder.add(productPath);
    if (fastspringBuilder.tag) {
      const tagPayload: Record<string, string> = {
        customOrderPackageKey: "custom-case",
      };
      if (customOrderId) tagPayload.customOrderId = customOrderId;
      if (accessToken) tagPayload.customOrderAccessToken = accessToken;
      if (landingContext?.lang) tagPayload.customOrderLanguage = landingContext.lang;
      if (landingContext?.topic) tagPayload.customOrderTopic = landingContext.topic;
      if (landingContext?.source) {
        tagPayload.customOrderAttributionSource = landingContext.source;
      }
      if (landingContext?.utmSource) tagPayload.customOrderUtmSource = landingContext.utmSource;
      if (landingContext?.utmMedium) tagPayload.customOrderUtmMedium = landingContext.utmMedium;
      if (landingContext?.utmCampaign) {
        tagPayload.customOrderUtmCampaign = landingContext.utmCampaign;
      }
      fastspringBuilder.tag(tagPayload);
    }
    fastspringBuilder.checkout();
    return {
      launched: true as const,
      method: "builder" as const,
      correlation: fastspringBuilder.tag ? ("tag" as const) : ("none" as const),
    };
  }

  if (checkoutUrl) {
    window.location.href = appendCheckoutQuery({
      checkoutUrl,
      customOrderId,
      accessToken,
      landingContext,
    });
    return { launched: true as const, method: "url" as const, correlation: "none" as const };
  }

  return { launched: false as const, method: "none" as const, correlation: "none" as const };
};
